import React, { useState } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { Spinner, Empty } from "@/components/Atoms";
import { Activity, AlertTriangle, Gauge, RefreshCw, Save, ShieldCheck, Wrench } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

export const HEALTH_META = {
    healthy: { label: "Healthy", cls: "text-emerald-300 border-emerald-700/50 bg-emerald-500/10", dot: "bg-emerald-400" },
    degraded: { label: "Slow", cls: "text-amber-300 border-amber-700/50 bg-amber-500/10", dot: "bg-amber-400" },
    critical: { label: "Critical", cls: "text-orange-300 border-orange-700/50 bg-orange-500/10", dot: "bg-orange-400" },
    down: { label: "Down", cls: "text-red-300 border-red-800/60 bg-red-500/10", dot: "bg-red-500" },
    unreachable: { label: "Unreachable", cls: "text-purple-300 border-purple-700/50 bg-purple-500/10", dot: "bg-purple-400" },
    pending: { label: "Pending", cls: "text-sky-300 border-sky-800/50 bg-sky-500/10", dot: "bg-sky-400" },
    stopped: { label: "Stopped", cls: "text-zinc-400 border-zinc-700 bg-zinc-800/40", dot: "bg-zinc-500" },
    suspended: { label: "Suspended", cls: "text-zinc-400 border-zinc-700 bg-zinc-800/40", dot: "bg-zinc-500" },
    unknown: { label: "Unknown", cls: "text-zinc-400 border-zinc-700 bg-zinc-800/40", dot: "bg-zinc-500" },
};

const meta = (h) => HEALTH_META[h] || HEALTH_META.unknown;
const fmtLat = (ms) => (ms == null ? "—" : ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${ms}ms`);
const fmtAgo = (iso) => {
    if (!iso) return "—";
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${Math.round(s)}s ago`;
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    return `${Math.round(s / 3600)}h ago`;
};

export function HealthBadge({ health, testid }) {
    const m = meta(health);
    return (
        <span data-testid={testid} className={`inline-flex items-center gap-1.5 px-2 py-0.5 border rounded-sm text-[11px] font-mono uppercase tracking-wider ${m.cls}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
            {m.label}
        </span>
    );
}

// Slim global banner — only renders when something is degraded/critical/down.
export function HealthBanner() {
    const { data } = useSWR("/api/admin/infra/health", fetcher, { refreshInterval: 15000 });
    const alerting = data?.summary?.alerting || [];
    const overall = data?.summary?.overall;
    if (!data || !["down", "critical", "degraded"].includes(overall)) return null;
    const m = meta(overall);
    const alertCount = alerting.length;
    return (
        <div data-testid="health-banner" className={`mb-px border ${m.cls} px-4 py-2 flex flex-wrap items-center gap-3 text-xs font-mono`}>
            <AlertTriangle size={13} />
            <span className="uppercase tracking-widest">{overall === "degraded" ? "Infrastructure degraded" : "Infrastructure problem"}</span>
            <span className="text-zinc-400 normal-case tracking-normal">
                {alertCount ? alerting.slice(0, 6).join(" · ") : "Latency above threshold — see Infrastructure → Health"}
                {alertCount > 6 ? ` +${alertCount - 6}` : ""}
            </span>
            <a href="/infrastructure" className="ml-auto text-cm-brand hover:text-cm-brand uppercase tracking-widest">Details →</a>
        </div>
    );
}

function CountPills({ counts }) {
    const order = ["down", "unreachable", "critical", "degraded", "pending", "healthy", "unknown"];
    return (
        <div className="flex flex-wrap gap-1.5">
            {order.filter((k) => counts?.[k]).map((k) => {
                const m = meta(k);
                return (
                    <span key={k} data-testid={`health-count-${k}`} className={`px-1.5 py-0.5 border rounded-sm text-[11px] font-mono ${m.cls}`}>
                        {m.label}: {counts[k]}
                    </span>
                );
            })}
        </div>
    );
}

function mergeCounts(countObjs) {
    const out = {};
    for (const obj of countObjs) {
        for (const [k, v] of Object.entries(obj)) {
            out[k] = (out[k] || 0) + v;
        }
    }
    return out;
}

// Compact panel for the Overview dashboard.
export function ServiceHealthPanel() {
    const { data, error, mutate } = useSWR("/api/admin/infra/health", fetcher, { refreshInterval: 15000 });
    if (error) return <Empty title="Could not load health" />;
    if (!data) return (
        <div className="cm-card p-5" data-testid="overview-health-card">
            <div className="flex items-center gap-2 mb-4">
                <Activity size={16} strokeWidth={1.5} className="text-zinc-400" />
                <div className="cm-h3">Service health</div>
            </div>
            <div className="flex items-center gap-2 text-zinc-500 text-xs"><Spinner /> Loading health…</div>
        </div>
    );
    const probs = (data.services || []).filter((s) => ["down", "critical", "degraded"].includes(s.health));
    const slowest = [...(data.services || [])]
        .filter((s) => s.latency_ms != null)
        .sort((a, b) => (b.latency_ms || 0) - (a.latency_ms || 0))
        .slice(0, 5);
    const allEntries = [...(data.services || []), ...(data.clouds || []),
        ...(data.souvera || []), ...(data.pmg || [])];
    const allProbs = allEntries.filter((e) => ["down", "critical", "degraded"].includes(e.health));
    const mergedCounts = mergeCounts([
        data.summary?.services || {},
        data.summary?.clouds || {},
        data.summary?.souvera || {},
        data.summary?.pmg || {},
    ]);
    return (
        <div className="cm-card p-5" data-testid="overview-health-card">
            <div className="flex items-center gap-2 mb-4">
                <Activity size={16} strokeWidth={1.5} className="text-zinc-400" />
                <div className="cm-h3">Infrastructure health</div>
                <a href="/infrastructure" className="ml-auto text-xs text-cm-brand hover:text-cm-brand font-mono uppercase tracking-wider">details →</a>
            </div>
            <div className="flex items-center justify-between gap-3 mb-3">
                <HealthBadge health={data.summary?.overall} testid="overview-health-overall" />
                <button onClick={() => mutate()} data-testid="overview-health-refresh" className="cm-btn-ghost text-[11px] px-2 py-1 flex items-center gap-1">
                    <RefreshCw size={11} /> {fmtAgo(data.generated_at)}
                </button>
            </div>
            <div className="space-y-1 mb-3">
                <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">All (services + clouds + Souvera + PMG)</div>
                <CountPills counts={mergedCounts} />
            </div>
            {allProbs.length > 0 ? (
                <div className="space-y-1 mt-3" data-testid="overview-health-problems">
                    {allProbs.slice(0, 6).map((s) => (
                        <div key={s.id} className="flex items-center justify-between gap-2 text-xs border-b border-zinc-900 py-1">
                            <span className="font-mono text-zinc-300 truncate">{s.kind || s.category} <span className="text-zinc-600">·</span> {s.name}</span>
                            <span className="flex items-center gap-2">
                                {s.latency_ms != null && <span className="text-zinc-500 font-mono">{fmtLat(s.latency_ms)}</span>}
                                <HealthBadge health={s.health} />
                            </span>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-xs text-emerald-300/80 flex items-center gap-1.5 mt-2" data-testid="overview-health-allgood">
                    <ShieldCheck size={13} /> All systems healthy.
                </div>
            )}
            {slowest.length > 0 && (
                <div className="mt-3 pt-2 border-t border-zinc-900">
                    <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Highest latency</div>
                    {slowest.map((s) => (
                        <div key={s.id} className="flex items-center gap-2 text-xs py-0.5">
                            <span className="font-mono text-zinc-400 w-32 truncate">{s.kind}</span>
                            <div className="flex-1 h-1 bg-zinc-800">
                                <div className={`h-full ${meta(s.health).dot}`} style={{ width: `${Math.min(100, ((s.latency_ms || 0) / (s.crit_ms || 4000)) * 100)}%` }} />
                            </div>
                            <span className="font-mono text-zinc-500 w-14 text-right">{fmtLat(s.latency_ms)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// Full Infrastructure → Health tab: services, clouds, souvera, PMG tables, threshold editor, alerts.
export function MonitoringTab() {
    const { data, error, mutate } = useSWR("/api/admin/infra/health", fetcher, { refreshInterval: 15000 });
    const { data: alerts } = useSWR("/api/admin/infra/monitoring/alerts?limit=30", fetcher, { refreshInterval: 30000 });
    if (error) return <Empty title="Could not load monitoring" />;
    if (!data) return <Spinner />;

    const pmgNodes = data.pmg || [];
    const souveraApps = data.souvera || [];
    const clouds = data.clouds || [];

    return (
        <div className="space-y-5" data-testid="monitoring-tab">
            <div className="cm-card p-3 text-xs text-zinc-400 bg-zinc-950 border border-zinc-800 leading-relaxed flex items-center gap-3">
                <Gauge size={14} className="text-cm-brand" />
                <span>Monitoring of all service VMs, clouds, Souvera apps and PMG mail gateways.
                    Refreshes every 15s. <span className="text-zinc-500">As of: {fmtAgo(data.generated_at)}</span></span>
                <div className="ml-auto flex items-center gap-2">
                    <HealthBadge health={data.summary?.overall} testid="monitoring-overall" />
                    <button onClick={() => mutate()} className="cm-btn-ghost text-[11px] px-2 py-1 flex items-center gap-1" data-testid="monitoring-refresh">
                        <RefreshCw size={11} /> Refresh
                    </button>
                </div>
            </div>

            <ThresholdEditor thresholds={data.thresholds} kinds={[...new Set((data.services || []).map((s) => s.kind))]} onSaved={mutate} />

            <AutoRepairControl />

            <ServiceAccessControl />

            <HealthTable title="Service VMs (actively probed)" rows={data.services} testid="monitoring-services" showLat />

            {clouds.length > 0 ? (
                <HealthTable title="Clouds (Nextcloud web/worker via heartbeat)" rows={clouds} testid="monitoring-clouds" showLat={false} />
            ) : (
                <div className="cm-card p-3 text-xs text-zinc-500 bg-zinc-950 border border-zinc-800" data-testid="monitoring-clouds-note">
                    Clouds — no active clouds or heartbeat data available.
                </div>
            )}

            {souveraApps.length > 0 && (
                <SouveraHealthTable rows={souveraApps} />
            )}

            {pmgNodes.length > 0 && (
                <PmgHealthTable rows={pmgNodes} />
            )}

            <div className="cm-card p-0 border border-zinc-800" data-testid="monitoring-alerts">
                <div className="px-3 py-2 text-xs uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-800 flex items-center gap-2">
                    <AlertTriangle size={12} /> Alert history
                </div>
                {(alerts || []).length === 0 ? (
                    <div className="px-3 py-6 text-center text-zinc-600 text-xs">No alerts.</div>
                ) : (
                    <div className="divide-y divide-zinc-900">
                        {(alerts || []).map((a) => (
                            a.event_type === "auto_repair" ? (
                                <div key={a.id} className="px-3 py-2 text-xs flex flex-wrap items-center gap-x-3 gap-y-1 font-mono" data-testid={`monitoring-alert-${a.id}`}>
                                    <span className="text-zinc-500">{fmtAgo(a.ts)}</span>
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border rounded-sm text-[11px] uppercase tracking-wider text-sky-300 border-sky-800/50 bg-sky-500/10"><Wrench size={10} /> Auto-Repair</span>
                                    <span className="text-zinc-300">{a.name}</span>
                                    <span className="text-zinc-600">{a.kind}</span>
                                    <span className="text-amber-300/80 uppercase text-[11px]">{a.action}</span>
                                    <span className="text-zinc-400 normal-case">{a.detail}</span>
                                </div>
                            ) : (
                                <div key={a.id} className="px-3 py-2 text-xs flex flex-wrap items-center gap-x-3 gap-y-1 font-mono" data-testid={`monitoring-alert-${a.id}`}>
                                    <span className="text-zinc-500">{fmtAgo(a.ts)}</span>
                                    <span className="text-zinc-300">{a.name}</span>
                                    <span className="text-zinc-600">{a.kind}</span>
                                    <HealthBadge health={a.from_state} /> <span className="text-zinc-600">→</span> <HealthBadge health={a.to_state} />
                                    {a.latency_ms != null && <span className="text-zinc-500">{fmtLat(a.latency_ms)}</span>}
                                </div>
                            )
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export function SouveraHealthTable({ rows }) {
    const sorted = [...rows].sort((a, b) => {
        const o = { down: 0, critical: 1, degraded: 2, unknown: 3, healthy: 4 };
        return (o[a.health] ?? 9) - (o[b.health] ?? 9);
    });
    return (
        <div className="cm-card p-0 border border-zinc-800 overflow-x-auto" data-testid="monitoring-souvera">
            <div className="px-3 py-2 text-xs uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-800">Souvera Apps ({sorted.length})</div>
            <table className="w-full text-xs font-mono">
                <thead>
                    <tr className="text-zinc-500 uppercase text-[11px] tracking-wider border-b border-zinc-800">
                        <th className="text-left px-3 py-2">Health</th>
                        <th className="text-left px-3 py-2">Name / Domain</th>
                        <th className="text-left px-3 py-2">Details</th>
                        <th className="text-left px-3 py-2">Last Check</th>
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((r) => (
                        <tr key={r.id} className="border-b border-zinc-900 hover:bg-zinc-900/40" data-testid={`monitoring-souvera-row-${r.id}`}>
                            <td className="px-3 py-2"><HealthBadge health={r.health} /></td>
                            <td className="px-3 py-2 text-zinc-300 max-w-[16rem] truncate">{r.name}</td>
                            <td className="px-3 py-2 text-zinc-500 max-w-[20rem] truncate" title={r.detail}>{r.detail || "—"}</td>
                            <td className="px-3 py-2 text-zinc-500">{fmtAgo(r.last_check)}</td>
                        </tr>
                    ))}
                    {sorted.length === 0 && (
                        <tr><td colSpan={4} className="px-3 py-6 text-center text-zinc-600">No entries</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

export function PmgHealthTable({ rows }) {
    const sorted = [...rows].sort((a, b) => {
        const o = { down: 0, critical: 1, degraded: 2, unknown: 3, healthy: 4 };
        return (o[a.health] ?? 9) - (o[b.health] ?? 9);
    });
    const fmtNum = (n) => (n != null ? n.toLocaleString() : "—");
    return (
        <div className="cm-card p-0 border border-zinc-800 overflow-x-auto" data-testid="monitoring-pmg">
            <div className="px-3 py-2 text-xs uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-800">PMG Mail Gateways ({sorted.length})</div>
            <table className="w-full text-xs font-mono">
                <thead>
                    <tr className="text-zinc-500 uppercase text-[11px] tracking-wider border-b border-zinc-800">
                        <th className="text-left px-3 py-2">Health</th>
                        <th className="text-left px-3 py-2">Node</th>
                        <th className="text-left px-3 py-2">Services</th>
                        <th className="text-left px-3 py-2">Queue</th>
                        <th className="text-left px-3 py-2">System</th>
                        <th className="text-left px-3 py-2">Last Check</th>
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((r) => {
                        const svc = r.services || {};
                        const q = r.queue || {};
                        const st = r.status || {};
                        return (
                            <tr key={r.id} className="border-b border-zinc-900 hover:bg-zinc-900/40" data-testid={`monitoring-pmg-row-${r.id}`}>
                                <td className="px-3 py-2"><HealthBadge health={r.health} /></td>
                                <td className="px-3 py-2 text-zinc-300 max-w-[14rem] truncate">{r.name}</td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                    {["postfix", "pmg-smtp-filter"].map((n) => {
                                        const ok = svc[n] === "running";
                                        return <span key={n} className={`mr-2 ${ok ? "text-emerald-300" : "text-red-400"}`}>{n}={svc[n] || "?"}</span>;
                                    })}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                    <span className="text-sky-300 mr-2">active={fmtNum(q.active)}</span>
                                    <span className={q.deferred > 500 ? "text-orange-300 mr-2" : "text-zinc-400 mr-2"}>deferred={fmtNum(q.deferred)}</span>
                                    <span className="text-zinc-500">in={fmtNum(q.incoming)}</span>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-zinc-400">
                                    {st.cpu != null ? `CPU ${(st.cpu * 100).toFixed(0)}%` : "—"}
                                    {st.memory_used != null && st.memory_total != null ? ` · RAM ${(st.memory_used / st.memory_total * 100).toFixed(0)}%` : ""}
                                </td>
                                <td className="px-3 py-2 text-zinc-500">{fmtAgo(r.last_check)}</td>
                            </tr>
                        );
                    })}
                    {sorted.length === 0 && (
                        <tr><td colSpan={6} className="px-3 py-6 text-center text-zinc-600">No PMG data — worker not run yet</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

export function HealthTable({ title, rows, testid, showLat, showCloud }) {
    const order = { down: 0, critical: 1, degraded: 2, pending: 3, unknown: 4, healthy: 5 };
    const sorted = [...(rows || [])].sort((a, b) => (order[a.health] ?? 9) - (order[b.health] ?? 9));
    return (
        <div className="cm-card p-0 border border-zinc-800 overflow-x-auto" data-testid={testid}>
            <div className="px-3 py-2 text-xs uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-800">{title} ({sorted.length})</div>
            <table className="w-full text-xs font-mono">
                <thead>
                    <tr className="text-zinc-500 uppercase text-[11px] tracking-wider border-b border-zinc-800">
                        <th className="text-left px-3 py-2">Health</th>
                        <th className="text-left px-3 py-2">Kind</th>
                        <th className="text-left px-3 py-2">Name</th>
                        {showCloud && <th className="text-left px-3 py-2">Cloud</th>}
                        {showLat && <th className="text-left px-3 py-2">Latenz</th>}
                        {showLat && <th className="text-left px-3 py-2">Threshold</th>}
                        <th className="text-left px-3 py-2">Status</th>
                        <th className="text-left px-3 py-2">{showLat ? "Last check" : "Heartbeat"}</th>
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((r) => (
                        <tr key={r.id} className="border-b border-zinc-900 hover:bg-zinc-900/40" data-testid={`${testid}-row-${r.id}`}>
                            <td className="px-3 py-2"><HealthBadge health={r.health} /></td>
                            <td className="px-3 py-2 text-zinc-300">{r.kind}</td>
                            <td className="px-3 py-2 text-zinc-400 max-w-[18rem] truncate" title={r.detail || r.name}>{r.name}</td>
                            {showCloud && (
                                <td className="px-3 py-2 text-zinc-500 max-w-[16rem] truncate" title={r.cloud_name || ""}>
                                    {r.cloud_name || <span className="text-zinc-700">geteilt</span>}
                                </td>
                            )}
                            {showLat && <td className="px-3 py-2 text-zinc-300">{fmtLat(r.latency_ms)}</td>}
                            {showLat && <td className="px-3 py-2 text-zinc-600">{r.warn_ms}/{r.crit_ms}ms</td>}
                            <td className="px-3 py-2 text-zinc-500">{r.status}</td>
                            <td className="px-3 py-2 text-zinc-500">{showLat ? fmtAgo(r.last_check) : (r.heartbeat_age_s != null ? `${r.heartbeat_age_s}s ago` : "—")}</td>
                        </tr>
                    ))}
                    {sorted.length === 0 && (
                        <tr><td colSpan={(showLat ? 7 : 5) + (showCloud ? 1 : 0)} className="px-3 py-6 text-center text-zinc-600">No entries</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

// Per-Tenant Service-Zugriff (Firewall-Gate): nur Kunden, die Collabora/HPB
// gebucht haben, dürfen netzwerkseitig drauf — ohne coolwsd/Signaling-Restart.
function ServiceAccessControl() {
    const { data, mutate } = useSWR("/api/admin/firewall/service-access", fetcher, { refreshInterval: 60000 });
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    const [err, setErr] = useState(null);
    const enforce = !!data?.enforce;
    const gated = data?.gated || {};

    const apply = async (next) => {
        const txt = next
            ? "ACTIVATE firewall gate?\n\nCollabora & Talk HPB will be network-blocked for ALL tenants — only clouds that have booked the service receive an ACCEPT rule. No service restart occurs. (Cluster-wide reconcile, may take a moment.)"
            : "DEACTIVATE firewall gate?\n\nAll tenants get open network access to Collabora & Talk HPB again.";
        if (!window.confirm(txt)) return;
        setBusy(true); setErr(null); setMsg(null);
        try {
            const r = await api.post("/api/admin/firewall/service-access/reconcile", { enforce: next });
            setMsg(`OK — ${r.data?.clouds ?? "?"} clouds reconciled (${next ? "on" : "off"}).`);
            mutate();
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally { setBusy(false); }
    };

    return (
        <div className="cm-card p-4 space-y-3 border border-zinc-800" data-testid="service-access-control">
            <div className="flex items-center gap-2">
                <ShieldCheck size={13} className="text-cm-brand" />
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Service access (firewall gate)</div>
                <span data-testid="service-access-status" className={`ml-2 inline-flex items-center px-2 py-0.5 border rounded-sm text-[11px] font-mono uppercase tracking-wider ${enforce ? "text-emerald-300 border-emerald-700/50 bg-emerald-500/10" : "text-zinc-400 border-zinc-700 bg-zinc-800/40"}`}>
                    {enforce ? "On (default-deny)" : "Off (open)"}
                </span>
            </div>
            <div className="text-xs text-zinc-400 leading-relaxed">
                Only tenants that have <span className="text-zinc-300">booked</span> a service may reach it over the network (Proxmox firewall, <span className="text-zinc-300">no restart</span>). Default-deny for Collabora &amp; Talk HPB.
            </div>
            <div className="flex flex-wrap gap-2">
                {Object.entries(gated).map(([kind, s]) => (
                    <span key={kind} className="text-[11px] font-mono px-2 py-1 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-300">
                        {kind}: <span className="text-emerald-300">{s.booked_clouds}</span> booked · <span className="text-sky-300">{s.service_vms}</span> VMs
                    </span>
                ))}
            </div>
            <div className="flex items-center gap-2 pt-1">
                <button onClick={() => apply(!enforce)} disabled={busy}
                    className={"text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-50 " + (enforce ? "cm-btn-secondary" : "cm-btn-primary")}
                    data-testid="service-access-toggle">
                    <ShieldCheck size={12} /> {busy ? "Applying…" : (enforce ? "Disable gate" : "Enable gate")}
                </button>
                <span className="text-xs text-zinc-500 flex-1">Applies a cluster-wide reconcile action to all web/worker VMs.</span>
            </div>
            {msg && <div className="text-emerald-300 text-xs" data-testid="service-access-msg">{msg}</div>}
            {err && <div className="text-red-400 text-xs font-mono break-all" data-testid="service-access-error">{err}</div>}
        </div>
    );
}

const REPAIR_MODE_META = {
    off: { label: "Off", cls: "text-zinc-400 border-zinc-700 bg-zinc-800/40", hint: "No automatic repair." },
    dry_run: { label: "Dry-Run", cls: "text-amber-300 border-amber-700/50 bg-amber-500/10", hint: "Only log (would repair) — NO intervention." },
    active: { label: "Active", cls: "text-emerald-300 border-emerald-700/50 bg-emerald-500/10", hint: "Actually repairs: re-push config + service reload/restart." },
};

// Auto-Repair Stufe 1 Steuerung (Config re-push + reload→restart, kein VM-Reboot).
function AutoRepairControl() {
    const { data, mutate } = useSWR("/api/admin/infra/monitoring/auto-repair", fetcher, { refreshInterval: 30000 });
    const [mode, setMode] = useState(null);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);
    const [err, setErr] = useState(null);
    const current = mode ?? data?.mode ?? "dry_run";

    const save = async () => {
        setSaving(true); setErr(null); setMsg(null);
        try {
            await api.put("/api/admin/infra/monitoring/auto-repair", { mode: current });
            setMsg("Saved ✓");
            mutate();
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally { setSaving(false); }
    };

    const m = REPAIR_MODE_META[current] || REPAIR_MODE_META.dry_run;
    return (
        <div className="cm-card p-4 space-y-3 border border-zinc-800" data-testid="auto-repair-control">
            <div className="flex items-center gap-2">
                <Wrench size={13} className="text-cm-brand" />
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Auto-Repair (Level 1)</div>
                <span data-testid="auto-repair-status" className={`ml-2 inline-flex items-center px-2 py-0.5 border rounded-sm text-[11px] font-mono uppercase tracking-wider ${m.cls}`}>{m.label}</span>
            </div>
            <div className="text-xs text-zinc-400 leading-relaxed">
                For <span className="text-amber-300">degraded</span>/<span className="text-red-300">down</span> (from the deep probe) on
                {" "}<span className="font-mono text-zinc-300">{(data?.repairable_kinds || ["talk-signaling", "coturn"]).join(", ")}</span>:
                re-push config + service reload→restart (no VM reboot).
                {" "}Max. <span className="text-zinc-300">{data?.max_attempts ?? 3}</span> attempts,
                {" "}Cooldown <span className="text-zinc-300">{Math.round((data?.cooldown_s ?? 600) / 60)} min</span>.
            </div>
            <div className="flex items-center gap-2 pt-1">
                <select value={current} onChange={(e) => setMode(e.target.value)} className="cm-input text-xs py-1.5 bg-zinc-900 border border-zinc-700" data-testid="auto-repair-mode-select">
                    <option value="off">Off</option>
                    <option value="dry_run">Dry-Run (recommended)</option>
                    <option value="active">Active</option>
                </select>
                <span className="text-xs text-zinc-500 flex-1">{m.hint}</span>
                <button onClick={save} disabled={saving} className="cm-btn-primary text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-50" data-testid="auto-repair-save">
                    <Save size={12} /> {saving ? "Saving…" : "Save"}
                </button>
            </div>
            {msg && <div className="text-emerald-300 text-xs">{msg}</div>}
            {err && <div className="text-red-400 text-xs font-mono break-all" data-testid="auto-repair-error">{err}</div>}
        </div>
    );
}

function ThresholdEditor({ thresholds, kinds, onSaved }) {
    const [def, setDef] = useState(thresholds.default);
    const [perKind, setPerKind] = useState(thresholds.kinds || {});
    const [addKind, setAddKind] = useState("");
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);
    const [err, setErr] = useState(null);

    const save = async () => {
        setSaving(true); setErr(null); setMsg(null);
        try {
            const body = {
                default: { warn_ms: Number(def.warn_ms), crit_ms: Number(def.crit_ms) },
                kinds: Object.fromEntries(Object.entries(perKind).map(([k, v]) => [k, { warn_ms: Number(v.warn_ms), crit_ms: Number(v.crit_ms) }])),
            };
            await api.put("/api/admin/infra/monitoring/thresholds", body);
            setMsg("Saved ✓");
            onSaved && onSaved();
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally { setSaving(false); }
    };

    const Pair = ({ label, val, on, testidPrefix }) => (
        <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-400 w-28 truncate" title={label}>{label}</span>
            <input type="number" value={val.warn_ms} data-testid={`${testidPrefix}-warn`} onChange={(e) => on({ ...val, warn_ms: e.target.value })} className="cm-input text-xs font-mono w-24" placeholder="warn ms" />
            <span className="text-zinc-600 text-xs">warn</span>
            <input type="number" value={val.crit_ms} data-testid={`${testidPrefix}-crit`} onChange={(e) => on({ ...val, crit_ms: e.target.value })} className="cm-input text-xs font-mono w-24" placeholder="crit ms" />
            <span className="text-zinc-600 text-xs">crit (ms)</span>
        </div>
    );

    const availKinds = (kinds || []).filter((k) => k && !(k in perKind));

    return (
        <div className="cm-card p-4 space-y-3 border border-zinc-800" data-testid="threshold-editor">
            <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Latency thresholds (probe response time)</div>
            <Pair label="Default (all)" val={def} on={setDef} testidPrefix="thr-default" />
            {Object.entries(perKind).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2">
                    <Pair label={k} val={v} on={(nv) => setPerKind({ ...perKind, [k]: nv })} testidPrefix={`thr-${k}`} />
                    <button onClick={() => { const c = { ...perKind }; delete c[k]; setPerKind(c); }} className="text-red-400 text-xs hover:text-red-300" data-testid={`thr-remove-${k}`}>remove</button>
                </div>
            ))}
            <div className="flex items-center gap-2 pt-1">
                <select value={addKind} onChange={(e) => setAddKind(e.target.value)} className="cm-input text-xs py-1.5 bg-zinc-900 border border-zinc-700" data-testid="thr-add-kind-select">
                    <option value="">+ Per-service override…</option>
                    {availKinds.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
                <button disabled={!addKind} onClick={() => { setPerKind({ ...perKind, [addKind]: { ...def } }); setAddKind(""); }} className="cm-btn-ghost text-xs px-2 py-1 disabled:opacity-40" data-testid="thr-add-kind-btn">add</button>
                <div className="flex-1" />
                <button onClick={save} disabled={saving} className="cm-btn-primary text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-50" data-testid="thr-save">
                    <Save size={12} /> {saving ? "Saving…" : "Save"}
                </button>
            </div>
            {msg && <div className="text-emerald-300 text-xs">{msg}</div>}
            {err && <div className="text-red-400 text-xs font-mono break-all" data-testid="thr-error">{err}</div>}
        </div>
    );
}
